import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Loader2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import EvidenceButton from './sitewalk/EvidenceButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RESULTS = ['LIKELY_PRESENT', 'POTENTIALLY_MISSING', 'POTENTIAL_DEVIATION', 'PARTIALLY_VISIBLE', 'INSUFFICIENT_EVIDENCE', 'UNABLE_TO_DETERMINE'];

const BimElementDrawer = ({ projectId, elementId, isAdmin, onClose, onChanged }) => {
  const [el, setEl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setEl(await axios.get(`${API}/projects/${projectId}/design/bim/elements/${elementId}`).then((r) => r.data)); }
    catch (e) { toast.error('Failed to load element'); } finally { setLoading(false); }
  }, [projectId, elementId]);
  useEffect(() => { load(); }, [load]);

  const runReality = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      (files || []).forEach((f) => fd.append('files', f));
      fd.append('elementIds', elementId);
      const r = await axios.post(`${API}/projects/${projectId}/design/bim/reality`, fd);
      toast.success(`Reality observation created (${r.data.reviewStatus})`);
      setFiles([]); await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const reviewReality = async (obsId, action) => {
    let reviewedResult = null, reason = null;
    if (action === 'CORRECT') { reviewedResult = prompt(`Corrected result (${RESULTS.join('/')}):`); if (!reviewedResult) return; reason = prompt('Reason:'); if (!reason) return; }
    if (action === 'REJECT') { reason = prompt('Reason:'); if (!reason) return; }
    try { await axios.post(`${API}/projects/${projectId}/design/bim/reality/${obsId}/review`, { action, reviewedResult, reason }); toast.success('Reviewed'); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Review failed'); }
  };

  const createIssue = async (obsId) => {
    try { const r = await axios.post(`${API}/projects/${projectId}/design/bim/reality/${obsId}/create-issue`, {}); toast.success('Issue created from reviewed observation'); window.open(`/app/projects/${projectId}/issues?issueId=${r.data.id}`, '_blank'); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Create issue failed'); }
  };

  const p = el?.activityProgress;
  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="bim-element-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-slate-50 h-full overflow-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-bold text-slate-900 truncate">{el ? (el.name || el.globalId) : 'BIM Element'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="element-drawer-close"><X className="w-5 h-5" /></button>
        </div>
        {loading || !el ? <div className="flex items-center justify-center py-24 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <div className="p-6 space-y-5">
            <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs text-slate-400">GlobalId</p><p className="font-mono text-xs">{el.globalId}</p></div>
              <div><p className="text-xs text-slate-400">IFC Type</p><p>{el.ifcType || '—'}</p></div>
              <div><p className="text-xs text-slate-400">Level</p><p>{el.level || '—'}</p></div>
              <div><p className="text-xs text-slate-400">Mapping</p><p>{el.mappingStatus || 'UNMAPPED'}</p></div>
            </div>

            <div data-testid="bim-360-evidence"><EvidenceButton projectId={projectId} kind="bim-element" id={elementId} label="360 Evidence (BIM-linked)" /></div>

            {p && (
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="element-progress">
                <h3 className="font-semibold text-slate-800 mb-2">Linked Activity Progress</h3>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div><p className="text-xs text-slate-400">Planned</p><p>{p.plannedProgress != null ? `${p.plannedProgress}%` : '—'}</p></div>
                  <div><p className="text-xs text-slate-400">Reported</p><p>{p.reportedProgress != null ? `${p.reportedProgress}%` : '—'}</p></div>
                  <div><p className="text-xs text-slate-400">AI Observed</p><p>{p.aiObservedProgress != null ? `${p.aiObservedProgress}%` : '—'}</p></div>
                  <div><p className="text-xs text-slate-400">Reviewed Visual</p><p>{p.reviewedVisualProgress != null ? `${p.reviewedVisualProgress}%` : '— No Evidence'}</p></div>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Progress comes from Phase 4 review — never computed from BIM geometry.</p>
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="element-reality">
              <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><Camera className="w-4 h-4" /> BIM-vs-Reality</h3>
              <p className="text-[11px] text-amber-700 mb-2">Context-linked comparison — the photo is NOT geometrically registered to the model.</p>
              {isAdmin && (
                <div className="flex items-center gap-2 mb-3">
                  <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files))} className="text-sm" data-testid="reality-files" />
                  <button onClick={runReality} disabled={busy} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg disabled:opacity-50" data-testid="reality-run-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Compare'}</button>
                </div>
              )}
              {(el.realityObservations || []).length === 0 ? <p className="text-xs text-slate-400">No reality observations yet.</p> : el.realityObservations.map((o) => (
                <div key={o.id} className="border-t border-slate-50 py-2" data-testid="element-reality-row">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{o.reviewStatus.replace(/_/g, ' ')}</span>
                    <span>AI: {o.aiResult || '—'}</span>{o.reviewedResult && <span className="text-emerald-700">Reviewed: {o.reviewedResult}</span>}
                  </div>
                  {isAdmin && o.reviewStatus === 'PENDING_REVIEW' && (
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {['ACCEPT', 'CORRECT', 'REJECT', 'INSUFFICIENT'].map((a) => <button key={a} onClick={() => reviewReality(o.id, a)} disabled={a === 'ACCEPT' && !o.aiResult} className="px-2 py-1 text-[11px] rounded bg-slate-700 text-white disabled:opacity-40" data-testid={`reality-${a.toLowerCase()}-btn`}>{a}</button>)}
                    </div>
                  )}
                  {['ACCEPTED', 'CORRECTED'].includes(o.reviewStatus) && (
                    <div className="mt-1 flex items-center gap-2">
                      <button onClick={() => createIssue(o.id)} className="px-2 py-1 text-[11px] rounded bg-rose-600 text-white" data-testid="reality-create-issue-btn">Create Issue</button>
                      {(o.issueIds || []).length > 0 && <span className="text-[10px] text-slate-400">{o.issueIds.length} issue(s) linked</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BimElementDrawer;
